import { Avatar, Badge, Box, Button, DialogBody, Flex, FormatNumber, Grid, GridItem, HStack, Image, Spinner, Stack, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, UI } from "components";
import { Pressable } from "components/ui";
import { API } from "config";
import { toaster } from "config";
import useKits from "hooks/useKits";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

export default function Buy({ ...props }){

    const submitRef = useRef()

    const kits = useKits()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const [ selected, setKit ] = useState(kits.data?.[0] ?? null)
    const [ quantity, setQuantity ] = useState(selected?.min_order ?? 5)
    const [ payment_method, setPayment ] = useState(1)
    const [ shipping_type, setShipping ] = useState(2)
    const [ loading, setLoading ] = useState(false)
    const [ quoted, setQuoted ] = useState()
    const enabled = !!quantity && payment_method && !!selected

    const handleChangeKit = kit => {
        if(quantity < kit.min_order) setQuantity(kit.min_order)
        if(kit.is_free) setShipping(2)
        setKit(kit)
    }

    const handleChangeQuantity = qtd => {
        if(qtd < selected?.min_order) return;
        if(selected?.max_order && qtd > selected?.max_order) return;
        setQuantity(qtd)
    }

    const handleSubmit = async () => {
        if(!enabled ) return
        setLoading('submit')

        try{
            const callback = await API.post('/store/cart/vouchers', { product_kit_id: selected.id, transaction_method: payment_method, quantity, shipping_type })
            toaster.create({ title: 'O pedido de vouchers foi realizado', type: 'success' })
            queryClient.invalidateQueries('vouchers')
            queryClient.invalidateQueries('orders')
            props.onClose()
            navigate(`/pedidos/${callback.data?.cod}`)
        }catch(err){
            toaster.create({ title: 'Não foi possível realizar o pedido agora.', description: err?.response?.data?.message, type: 'warning' })
        }

        setLoading(false)
    }

    const handleQuote = async () => {
        if(!enabled) return
        setLoading('quote')

        try{
            const callback = await API.get(`/store/cart/vouchers/quote?product_kit_id=${selected.id}&quantity=${quantity}&shipping_type=${shipping_type}`)
            setQuoted(callback.data)
        }catch(err){
            toaster.create({ title: 'Não foi possível cotar o frete do pedido. Tente novamente.', description: err?.response?.data?.message, type: 'warning' })
        }
        setLoading(false)
    }

    useEffect(() => {
        if(enabled) handleQuote()
    }, [selected, quantity, shipping_type])

    return(
    <UI.Dialog title="Loja de vouchers" loading={!!loading} {...props}>
        <DialogBody>
            <Stack gap={4}>

                <UI.Alert colorPalette="gray" size="sm">
                    Os <b>Vouchers</b> só podem ser comprados da <b>Matriz</b> e são utilizados para ativar novos consultores durante o processo de adesão.
                </UI.Alert>

                <Form.Container submitRef={submitRef} onSubmit={handleSubmit}>

                    <Text color="GrayText">Selecione o kit:</Text>

                    <Grid templateColumns={{ base: '1fr', sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr' }} gap={4}>
                        { kits.data?.map(item => <GridItem key={item.id}><Kit selected={selected?.id === item.id} data={item} onClick={() => handleChangeKit(item)} disabled={!!loading} /></GridItem> ) }
                    </Grid>

                    <UI.Show visible={selected?.is_free}>
                        <UI.Alert size="sm">O kit selecionado é considerado um <b>voucher gratuito</b>. Esse tipo de voucher não gera pontos e bônus e não há produtos.</UI.Alert>
                    </UI.Show>

                    <Form.Text
                        required
                        type="number"
                        label="Quantidade:"
                        defaultValue={selected?.min_order ?? 5}
                        value={quantity}
                        onChange={e => handleChangeQuantity(e.target.value)}
                        min={selected?.min_order ?? 5}
                        max={selected?.max_order ?? 100}
                        fontWeight="bold"
                        size="xl"
                        helper={`A quantidade mínima de compra deste voucher é de ${selected?.min_order ?? 5} unidades por pedido.`}
                        disabled={!!loading}
                    />

                    <Text color="GrayText">Forma de pagamento:</Text>

                    <Grid templateColumns="1fr 1fr" gap={4}>
                        <GridItem><PaymentMethod selected={payment_method === 1} data={{ label: 'Bônus', icon: 'bonus', colorPalette: 'orange' }} onClick={() => setPayment(1)} disabled={!!loading} /></GridItem>
                        {/*<GridItem><PaymentMethod selected={payment_method === 5} data={{ label: 'Mercado Pago', icon: 'mercadopago', colorPalette: 'blue' }} onClick={() => setPayment(5)} disabled={!!loading} /></GridItem>*/}
                        <GridItem>
                            <PaymentMethod
                                selected={payment_method === 9}
                                data={{ label: 'Cielo', icon: 'cielo', colorPalette: 'orange' }}
                                onClick={() => setPayment(9)} disabled={!!loading}
                            />
                        </GridItem>
                    </Grid>

                    <UI.Show visible={!selected?.is_free}>
                        <Text color="GrayText">Tipo de entrega:</Text>

                        <Grid templateColumns="1fr 1fr" gap={4}>
                            <GridItem><ShippingType selected={shipping_type === 1} data={{ label: 'Para entrega', icon: 'shipping', colorPalette: 'orange' }} onClick={() => setShipping(1)} disabled={!!loading} /></GridItem>
                            <GridItem><ShippingType selected={shipping_type === 2} data={{ label: 'Retirada', icon: 'withdraw', colorPalette: 'purple' }} onClick={() => setShipping(2)} disabled={!!loading} /></GridItem>
                        </Grid>
                    </UI.Show>

                    <Box>
                        <Text color="GrayText" mb={2}>Valor total:</Text>
                        <UI.Show visible={loading !== 'quote'}>
                            <Text color="green" fontSize="lg" fontWeight="bold"><FormatNumber value={quoted?.total_amount/100} style="currency" currency="BRL" /></Text>
                            <UI.Show visible={quoted?.shipping_amount}><Text color="GrayText" size="xs"><UI.Icon name="shipping" /> Valor da entrega: <FormatNumber value={quoted?.shipping_amount/100} style="currency" currency="BRL" /></Text></UI.Show>
                        </UI.Show>
                        <UI.Show visible={loading === 'quote'}><Spinner /></UI.Show>
                    </Box>

                    <Button size="lg" colorPalette="orange" onClick={() => submitRef.current.click()} disabled={!enabled || !!loading} loading={loading==='submit'} loadingText="Realizando pedido..."><UI.Icon name="voucher" /> Comprar vouchers</Button>
                </Form.Container>

            </Stack>
        </DialogBody>
    </UI.Dialog>
    )
}

const Kit = ({ data, selected, ...props }) => {
    return(
    <UI.Pressable {...props}>
        <Box borderWidth={2} borderRadius={3} borderColor={selected ? 'orange' : null}>
            <Image src={data?.image_url} />
            <Box p={4}>
                <Text>{data?.label}</Text>
                <Text fontSize="xs" color="GrayText">{data?.tagline}</Text>
                { data?.is_free ? <Badge>Não pontua</Badge> : <Badge colorPalette="teal">{data?.points / 100} pontos na adesão</Badge> }
                { selected && <Badge children="Selecionado" mt={2} />}
            </Box>
        </Box>
    </UI.Pressable>
    )
}

const PaymentMethod = ({ data, selected = false, ...props }) => {

    const color = `${data.colorPalette}.fg`

    return(
    <Pressable {...props}>
        <Flex
            flex={1}
            py={2}
            direction="column"
            alignItems="center"
            justifyContent="center"
            borderWidth={1}
            borderRadius={4}
            cursor="pointer"
            backgroundColor={selected ? 'bg.muted' : null}
            borderColor={selected ? color : null}
        >
            <HStack textAlign="center">
                <UI.Icon fontSize="2.6rem" name={data.icon} color={color} mb={2} />
                <Text fontWeight="bold">{data.label}</Text>
            </HStack>
        </Flex>
    </Pressable>
    )
}

const ShippingType = ({ data, selected = false, ...props }) => {

    const color = `${data.colorPalette}.fg`

    return(
    <Pressable {...props}>
        <Flex
            flex={1}
            py={2}
            direction="column"
            alignItems="center"
            justifyContent="center"
            borderWidth={1}
            borderRadius={4}
            cursor="pointer"
            backgroundColor={selected ? 'bg.muted' : null}
            borderColor={selected ? color : null}
        >
            <HStack textAlign="center">
                <UI.Icon fontSize="1.8rem" name={data.icon} color={color} mb={2} />
                <Text fontWeight="bold">{data.label}</Text>
            </HStack>
        </Flex>
    </Pressable>
    )
}
